import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ROUTE } from "../../routes";
import { Box } from "../../components/Box";
import { Paragraph } from "../../components/Paragraph";
import { Flex } from "../../components/Flex";
import styled from "styled-components";
import { Image } from "../../components/Image";
import open from "../../assets/settings/expansion-open.svg";
import close from "../../assets/settings/expansion-close.svg";
import general from "../../assets/settings/setting.svg";
import application from "../../assets/settings/application.svg";
import properties from "../../assets/settings/properties.svg";
import accountAccess from "../../assets/settings/person.svg";
import { Modal } from "../../components/Modal";
import { useUnsavedChangesNavigation } from "../../hooks/useUnsavedChangesNavigation";
import { useBillingInfo } from "../../data/useBillingInfo";

const StyledUl = styled.ul`
  ${({ theme: { fontSizes, fontFamily } }) => `
      list-style-type: none;
      margin-top: auto;
      margin: 0;
      list-style: none;
      font-family: ${fontFamily};
      font-size: ${fontSizes[1]};
      list-style-type: none;
      padding: 0;
    `}
`;

export const StyledLi = styled.li`
  ${({ theme: { space } }) => `
        user-select: none;
        margin-left: ${space[2]};
        display: flex;
        align-items: center;  
  `}
`;

export const StyledLink = styled(Link)`
  ${({ theme: { colors, radii, space } }) => `
        text-decoration: none;
        color: ${colors.gray[60]};
        transition: color .1s ease;
        padding: ${space[1]} ;

        &:hover {
          background-color: ${colors.gray[10]};
          border-radius: ${radii[0]};
        }

        &.active {
          color: ${colors.blue.primary};
          font-weight: bold;
        }
    `}
`;

export const StyleParagraph = styled(Paragraph)`
  ${({ theme: { space, fontSizes } }) => `
      margin-top: ${space[1]};
      margin-bottom: ${space[1]};      
      font-weight: 800;
      font-size: ${fontSizes[2]};
      align-items: center;
      display: flex;
  `}
`;
export const StyleDIV = styled(Flex)`
  ${({ theme: { colors, space, radii } }) => `
      margin-bottom: ${space[0]};
      padding-left: ${space[0]};
      cursor: pointer;
      justify-content: space-between;
      align-items: center;
      margin-top: ${space[0]};

      &.active {
        color: ${colors.blue.primary};
        background-color: ${colors.gray[10]};
        border-radius: ${radii[0]};        
      }
  `}
`;

const LeftMenu = () => {
  const [generalOpen, setGeneralOpen] = useState(true);
  const [applicationOpen, setApplicationOpen] = useState(false);
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const [accountAccessOpen, setAccountAccessOpen] = useState(false);
  const location = useLocation();

  const { data: billingData, isLoading: isLoadingBilling } = useBillingInfo();
  const profilesEnabled =
    billingData?.data?.results?.[0]?.profiles_enabled ?? true;

  const {
    handleNavigation,
    handleSaveChanges,
    handleLeave,
    showModal,
    setShowModal,
  } = useUnsavedChangesNavigation();

  useEffect(() => {
    if (!isLoadingBilling && !profilesEnabled) {
      setPropertiesOpen(false);
    }

    if (
      location.pathname === ROUTE.SETTINGS_PEP_SANCTION_SCREENING ||
      location.pathname === ROUTE.SETTINGS_SOF_RISK_SCORING ||
      location.pathname.includes(ROUTE.SETTINGS_APPLICATION_TYPES) ||
      location.pathname.includes(
        ROUTE.SETTINGS_ADDITIONAL_DOCUMENTS_REQUESTS
      ) ||
      location.pathname.includes(ROUTE.SETTINGS_FORMS) ||
      location.pathname.includes(ROUTE.SETTINGS_INCOME_AND_EMPLOYMENT) ||
      location.pathname.includes(ROUTE.SETTINGS_ADDRESS_HISTORY)
    ) {
      setApplicationOpen(true);
      setGeneralOpen(false);
      setPropertiesOpen(false);
      setAccountAccessOpen(false);
      return;
    }

    if (
      !isLoadingBilling &&
      profilesEnabled &&
      (location.pathname.includes(ROUTE.SETTINGS_PROPERTIES) ||
        location.pathname.includes(ROUTE.SETTINGS_PROPERTY_ADDRESS_TYPES) ||
        location.pathname.includes(ROUTE.SETTINGS_PROPERTY_INDUSTRIES) ||
        location.pathname.includes(ROUTE.SETTINGS_PROPERTY_SECTORS) ||
        location.pathname.includes(ROUTE.SETTINGS_PROPERTY_COUNTRY) ||
        location.pathname.includes(ROUTE.SETTINGS_PROPERTY_ENTITY_TYPES) ||
        location.pathname.includes(ROUTE.SETTINGS_PROPERTY_RELATIONSHIP_TYPES))
    ) {
      setPropertiesOpen(true);
      setGeneralOpen(false);
      setApplicationOpen(false);
      setAccountAccessOpen(false);
      return;
    }

    if (
      location.pathname === ROUTE.SETTINGS_USERS ||
      location.pathname.includes(ROUTE.SETTINGS_DIVISIONS_TEAMS) ||
      location.pathname.includes(ROUTE.SETTINGS_ROLES_PERMISSIONS)
    ) {
      setAccountAccessOpen(true);
      setGeneralOpen(false);
      setApplicationOpen(false);
      setPropertiesOpen(false);
      return;
    }

    setApplicationOpen(false);
    setPropertiesOpen(false);
    setAccountAccessOpen(false);
    setGeneralOpen(true);
  }, [location.pathname, isLoadingBilling, profilesEnabled]);

  const toggleGeneral = () => setGeneralOpen((v) => !v);
  const toggleApplication = () => setApplicationOpen((v) => !v);
  const toggleAccountAccess = () => setAccountAccessOpen((v) => !v);

  const toggleProperties = () => {
    if (!profilesEnabled) return;
    setPropertiesOpen((v) => !v);
  };

  return (
    <>
      <Box>
        {/* ---------- General ---------- */}
        <StyleDIV
          onClick={toggleGeneral}
          className={
            location.pathname === ROUTE.SETTINGS ||
            location.pathname === ROUTE.SETTINGS_BILLING_DETAILS ||
            location.pathname === ROUTE.SETTINGS_DPO_DETAILS ||
            location.pathname.startsWith(ROUTE.SETTINGS_BRANDING) ||
            location.pathname === ROUTE.SETTINGS_CONSULTANTS
              ? "active"
              : ""
          }
        >
          <StyleParagraph>
            <Image
              src={general}
              alt="general icon"
              height="24px"
              width="24px"
              marginRight={"4px"}
            />
            General
          </StyleParagraph>

          <Paragraph my={1}>
            {generalOpen ? (
              <Image
                src={close}
                alt="close"
                height="24px"
                width="24px"
                marginRight={"12px"}
              />
            ) : (
              <Image
                src={open}
                alt="open"
                height="24px"
                width="24px"
                marginRight={"12px"}
              />
            )}
          </Paragraph>
        </StyleDIV>

        {generalOpen && (
          <StyledUl>
            <StyledLi>
              <StyledLink
                to={ROUTE.SETTINGS}
                onClick={(e) => handleNavigation(e, ROUTE.SETTINGS)}
                className={location.pathname === ROUTE.SETTINGS ? "active" : ""}
              >
                Company
              </StyledLink>
            </StyledLi>

            <StyledLi>
              <StyledLink
                to={ROUTE.SETTINGS_BRANDING}
                onClick={(e) => handleNavigation(e, ROUTE.SETTINGS_BRANDING)}
                className={
                  location.pathname.startsWith(ROUTE.SETTINGS_BRANDING) ? "active" : ""
                }
              >
                Branding
              </StyledLink>
            </StyledLi>

            <StyledLi>
              <StyledLink
                onClick={(e) =>
                  handleNavigation(e, ROUTE.SETTINGS_BILLING_DETAILS)
                }
                to={ROUTE.SETTINGS_BILLING_DETAILS}
                className={
                  location.pathname === ROUTE.SETTINGS_BILLING_DETAILS
                    ? "active"
                    : ""
                }
              >
                Billing
              </StyledLink>
            </StyledLi>

            <StyledLi>
              <StyledLink
                onClick={(e) => handleNavigation(e, ROUTE.SETTINGS_CONSULTANTS)}
                to={ROUTE.SETTINGS_CONSULTANTS}
                className={
                  location.pathname === ROUTE.SETTINGS_CONSULTANTS
                    ? "active"
                    : ""
                }
              >
                Consultants
              </StyledLink>
            </StyledLi>

            <StyledLi>
              <StyledLink
                onClick={(e) => handleNavigation(e, ROUTE.SETTINGS_DPO_DETAILS)}
                to={ROUTE.SETTINGS_DPO_DETAILS}
                className={
                  location.pathname === ROUTE.SETTINGS_DPO_DETAILS
                    ? "active"
                    : ""
                }
              >
                Data retention
              </StyledLink>
            </StyledLi>
          </StyledUl>
        )}

        {/* ---------- Account & Access ---------- */}
        <StyleDIV
          onClick={toggleAccountAccess}
          className={
            location.pathname === ROUTE.SETTINGS_USERS ||
            location.pathname.includes(ROUTE.SETTINGS_DIVISIONS_TEAMS) ||
            location.pathname.includes(ROUTE.SETTINGS_ROLES_PERMISSIONS)
              ? "active"
              : ""
          }
        >
          <StyleParagraph>
            <Image
              src={accountAccess}
              alt="account and access icon"
              height="22px"
              width="24px"
              marginRight={"4px"}
            />
            Account & Access
          </StyleParagraph>

          <Paragraph my={1}>
            {accountAccessOpen ? (
              <Image
                src={close}
                alt="indicator icon"
                height="24px"
                width="24px"
                marginRight={"12px"}
              />
            ) : (
              <Image
                src={open}
                alt="indicator icon"
                height="24px"
                width="24px"
                marginRight={"12px"}
              />
            )}
          </Paragraph>
        </StyleDIV>

        {accountAccessOpen && (
          <StyledUl>
            <StyledLi>
              <StyledLink
                to={ROUTE.SETTINGS_USERS}
                onClick={(e) => handleNavigation(e, ROUTE.SETTINGS_USERS)}
                className={
                  location.pathname === ROUTE.SETTINGS_USERS ? "active" : ""
                }
              >
                Users & Access
              </StyledLink>
            </StyledLi>

            <StyledLi>
              <StyledLink
                onClick={(e) =>
                  handleNavigation(e, ROUTE.SETTINGS_DIVISIONS_TEAMS)
                }
                to={ROUTE.SETTINGS_DIVISIONS_TEAMS}
                className={
                  location.pathname.includes(ROUTE.SETTINGS_DIVISIONS_TEAMS)
                    ? "active"
                    : ""
                }
              >
                Divisions & Teams
              </StyledLink>
            </StyledLi>

            <StyledLi>
              <StyledLink
                onClick={(e) =>
                  handleNavigation(e, ROUTE.SETTINGS_ROLES_PERMISSIONS)
                }
                to={ROUTE.SETTINGS_ROLES_PERMISSIONS}
                className={
                  location.pathname.includes(ROUTE.SETTINGS_ROLES_PERMISSIONS)
                    ? "active"
                    : ""
                }
              >
                Roles & Permissions
              </StyledLink>
            </StyledLi>
          </StyledUl>
        )}

        {/* ---------- Application ---------- */}
        <StyleDIV
          onClick={toggleApplication}
          className={
            location.pathname.includes(ROUTE.SETTINGS_APPLICATION_TYPES) ||
            location.pathname === ROUTE.SETTINGS_PEP_SANCTION_SCREENING ||
            location.pathname === ROUTE.SETTINGS_SOF_RISK_SCORING ||
            location.pathname ===
              ROUTE.SETTINGS_ADDITIONAL_DOCUMENTS_REQUESTS ||
            location.pathname.includes(ROUTE.SETTINGS_FORMS) ||
            location.pathname.includes(ROUTE.SETTINGS_INCOME_AND_EMPLOYMENT) ||
            location.pathname.includes(ROUTE.SETTINGS_ADDRESS_HISTORY)
              ? "active"
              : ""
          }
        >
          <StyleParagraph>
            <Image
              src={application}
              alt="indicator icon"
              height="22px"
              width="24px"
              marginRight={"4px"}
            />
            Application
          </StyleParagraph>

          <Paragraph my={1}>
            {applicationOpen ? (
              <Image
                src={close}
                alt="indicator icon"
                height="24px"
                width="24px"
                marginRight={"12px"}
              />
            ) : (
              <Image
                src={open}
                alt="indicator icon"
                height="24px"
                width="24px"
                marginRight={"12px"}
              />
            )}
          </Paragraph>
        </StyleDIV>

        {applicationOpen && (
          <StyledUl>
            <StyledLi>
              <StyledLink
                onClick={(e) =>
                  handleNavigation(e, ROUTE.SETTINGS_APPLICATION_TYPES)
                }
                to={ROUTE.SETTINGS_APPLICATION_TYPES}
                className={
                  location.pathname.includes(ROUTE.SETTINGS_APPLICATION_TYPES)
                    ? "active"
                    : ""
                }
              >
                Application types
              </StyledLink>
            </StyledLi>

            <StyledLi>
              <StyledLink
                onClick={(e) =>
                  handleNavigation(e, ROUTE.SETTINGS_PEP_SANCTION_SCREENING)
                }
                to={ROUTE.SETTINGS_PEP_SANCTION_SCREENING}
                className={
                  location.pathname === ROUTE.SETTINGS_PEP_SANCTION_SCREENING
                    ? "active"
                    : ""
                }
              >
                Screening preferences
              </StyledLink>
            </StyledLi>

            <StyledLi>
              <StyledLink
                onClick={(e) =>
                  handleNavigation(e, ROUTE.SETTINGS_SOF_RISK_SCORING)
                }
                to={ROUTE.SETTINGS_SOF_RISK_SCORING}
                className={
                  location.pathname === ROUTE.SETTINGS_SOF_RISK_SCORING
                    ? "active"
                    : ""
                }
              >
                Source of funds
              </StyledLink>
            </StyledLi>

            <StyledLi>
              <StyledLink
                onClick={(e) =>
                  handleNavigation(
                    e,
                    ROUTE.SETTINGS_ADDITIONAL_DOCUMENTS_REQUESTS
                  )
                }
                to={ROUTE.SETTINGS_ADDITIONAL_DOCUMENTS_REQUESTS}
                className={
                  location.pathname ===
                  ROUTE.SETTINGS_ADDITIONAL_DOCUMENTS_REQUESTS
                    ? "active"
                    : ""
                }
              >
                Additional documents
              </StyledLink>
            </StyledLi>

            <StyledLi>
              <StyledLink
                onClick={(e) => handleNavigation(e, ROUTE.SETTINGS_FORMS)}
                to={ROUTE.SETTINGS_FORMS}
                className={
                  location.pathname.includes(ROUTE.SETTINGS_FORMS)
                    ? "active"
                    : ""
                }
              >
                Forms
              </StyledLink>
            </StyledLi>

            <StyledLi>
              <StyledLink
                onClick={(e) =>
                  handleNavigation(e, ROUTE.SETTINGS_ADDRESS_HISTORY)
                }
                to={ROUTE.SETTINGS_ADDRESS_HISTORY}
                className={
                  location.pathname.includes(ROUTE.SETTINGS_ADDRESS_HISTORY)
                    ? "active"
                    : ""
                }
              >
                Address History
              </StyledLink>
            </StyledLi>
          </StyledUl>
        )}

        {/* ---------- Properties (✅ COMPLETELY HIDDEN when profiles_enabled is false) ---------- */}
        {!isLoadingBilling && profilesEnabled && (
          <>
            <StyleDIV
              onClick={toggleProperties}
              className={
                location.pathname.includes(ROUTE.SETTINGS_PROPERTIES) ||
                location.pathname.includes(
                  ROUTE.SETTINGS_PROPERTY_ADDRESS_TYPES
                ) ||
                location.pathname.includes(
                  ROUTE.SETTINGS_PROPERTY_INDUSTRIES
                ) ||
                location.pathname.includes(ROUTE.SETTINGS_PROPERTY_SECTORS) ||
                location.pathname.includes(ROUTE.SETTINGS_PROPERTY_COUNTRY) ||
                location.pathname.includes(
                  ROUTE.SETTINGS_PROPERTY_ENTITY_TYPES
                ) ||
                location.pathname.includes(
                  ROUTE.SETTINGS_PROPERTY_RELATIONSHIP_TYPES
                )
                  ? "active"
                  : ""
              }
            >
              <StyleParagraph>
                <Image
                  src={properties}
                  alt="properties icon"
                  height="22px"
                  width="24px"
                  marginRight={"4px"}
                />
                Properties
              </StyleParagraph>

              <Paragraph my={1}>
                {propertiesOpen ? (
                  <Image
                    src={close}
                    alt="indicator icon"
                    height="24px"
                    width="24px"
                    marginRight={"12px"}
                  />
                ) : (
                  <Image
                    src={open}
                    alt="indicator icon"
                    height="24px"
                    width="24px"
                    marginRight={"12px"}
                  />
                )}
              </Paragraph>
            </StyleDIV>

            {propertiesOpen && (
              <StyledUl>
                <StyledLi>
                  <StyledLink
                    onClick={(e) =>
                      handleNavigation(e, ROUTE.SETTINGS_PROPERTY_ADDRESS_TYPES)
                    }
                    to={ROUTE.SETTINGS_PROPERTY_ADDRESS_TYPES}
                    className={
                      location.pathname.includes(
                        ROUTE.SETTINGS_PROPERTY_ADDRESS_TYPES
                      )
                        ? "active"
                        : ""
                    }
                  >
                    Address types
                  </StyledLink>
                </StyledLi>

                <StyledLi>
                  <StyledLink
                    onClick={(e) =>
                      handleNavigation(e, ROUTE.SETTINGS_PROPERTY_INDUSTRIES)
                    }
                    to={ROUTE.SETTINGS_PROPERTY_INDUSTRIES}
                    className={
                      location.pathname.includes(
                        ROUTE.SETTINGS_PROPERTY_INDUSTRIES
                      )
                        ? "active"
                        : ""
                    }
                  >
                    Industries
                  </StyledLink>
                </StyledLi>

                <StyledLi>
                  <StyledLink
                    onClick={(e) =>
                      handleNavigation(e, ROUTE.SETTINGS_PROPERTY_SECTORS)
                    }
                    to={ROUTE.SETTINGS_PROPERTY_SECTORS}
                    className={
                      location.pathname.includes(
                        ROUTE.SETTINGS_PROPERTY_SECTORS
                      )
                        ? "active"
                        : ""
                    }
                  >
                    Sectors
                  </StyledLink>
                </StyledLi>

                <StyledLi>
                  <StyledLink
                    onClick={(e) =>
                      handleNavigation(e, ROUTE.SETTINGS_PROPERTY_ENTITY_TYPES)
                    }
                    to={ROUTE.SETTINGS_PROPERTY_ENTITY_TYPES}
                    className={
                      location.pathname.includes(
                        ROUTE.SETTINGS_PROPERTY_ENTITY_TYPES
                      )
                        ? "active"
                        : ""
                    }
                  >
                    Entity types
                  </StyledLink>
                </StyledLi>

                <StyledLi>
                  <StyledLink
                    onClick={(e) =>
                      handleNavigation(e, ROUTE.SETTINGS_PROPERTY_COUNTRY)
                    }
                    to={ROUTE.SETTINGS_PROPERTY_COUNTRY}
                    className={
                      location.pathname.includes(
                        ROUTE.SETTINGS_PROPERTY_COUNTRY
                      )
                        ? "active"
                        : ""
                    }
                  >
                    Country
                  </StyledLink>
                </StyledLi>

                <StyledLi>
                  <StyledLink
                    onClick={(e) =>
                      handleNavigation(
                        e,
                        ROUTE.SETTINGS_PROPERTY_RELATIONSHIP_TYPES
                      )
                    }
                    to={ROUTE.SETTINGS_PROPERTY_RELATIONSHIP_TYPES}
                    className={
                      location.pathname.includes(
                        ROUTE.SETTINGS_PROPERTY_RELATIONSHIP_TYPES
                      )
                        ? "active"
                        : ""
                    }
                  >
                    Profile relationship types
                  </StyledLink>
                </StyledLi>
              </StyledUl>
            )}
          </>
        )}
      </Box>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        width="450px"
        title={"You have unsaved changes"}
        content={
          "You have made changes to your settings that have not been saved. Do you want to save your changes before leaving this page?"
        }
        primaryButtonText="Save"
        primaryButtonVariant="primary"
        secondaryButtonText="Leave"
        secondaryButtonVariant="danger-text"
        hasSecondaryActionButton
        onModalConfirmClick={handleSaveChanges}
        onSecondaryButtonClick={handleLeave}
      />
    </>
  );
};

export default LeftMenu;
